import { formatCents, formatCount, formatPercent, tt } from '@agos/shared'

/**
 * Cashback for organizations - `FRONTEND.md` §9.782, `BACKEND.md` §10A.436, owner O138g.
 *
 * One code for all of an organization's supporters: the customer gets a percentage off, the
 * organization a percentage of the food, and the restaurant pays the organization over by hand. The
 * server writes the coupon, records what each order earned and keeps the ledger; this reads the
 * answers and checks a form against the same rules before it is sent, so an operator is told what is
 * wrong before the server refuses it.
 *
 * Money arrives in cents, as whole numbers, and is added here in whole numbers.
 */

export interface CashbackOrganization {
  id: number
  name: string
  couponCode: string
  customerPercent: number
  organizationPercent: number
  active: boolean
  /** The last day the code works, `YYYY-MM-DD`; null runs until switched off. */
  endsOn: string | null
  note: string
  orders: number
  owedCents: number
  paidCents: number
  balanceCents: number
  /** An order has used the code, so the code cannot change any more. */
  used: boolean
}

export interface ReportBucket {
  orders: number
  foodCents: number
  shareCents: number
}

export type BucketKind = 'owed' | 'pending' | 'cancelled'

export interface ReportOrder {
  orderId: number
  orderNumber: string
  createdAt: string
  status: string
  bucket: BucketKind
  foodCents: number
  organizationPercent: number
  shareCents: number
}

export interface Payout {
  id: number
  amountCents: number
  paidOn: string
  note: string
  recordedBy: string
}

export interface CashbackReport {
  organization: CashbackOrganization
  from: string
  to: string
  owed: ReportBucket
  pending: ReportBucket
  cancelled: ReportBucket
  orders: ReportOrder[]
  truncated: boolean
  payouts: Payout[]
}

export const ORGANIZATIONS_PATH = '/api/v1/cashback-organizations'

export function organizationPath(id: number): string {
  return `${ORGANIZATIONS_PATH}/${String(id)}`
}

export function reportPath(id: number, from: string, to: string): string {
  return `${organizationPath(id)}/report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
}

export function payoutsPath(id: number): string {
  return `${organizationPath(id)}/payouts`
}

export function payoutPath(id: number, payoutId: number): string {
  return `${payoutsPath(id)}/${String(payoutId)}`
}

// ─── Reading the answers ──────────────────────────────────────────────────

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function whole(value: unknown): number {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(parsed) ? Math.round(parsed) : 0
}

function decimal(value: unknown): number {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(parsed) ? parsed : 0
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** One organization, or null for an answer that is not one - so a list never draws a blank row. */
export function organizationOf(payload: unknown): CashbackOrganization | null {
  const row = record(payload)
  if (row === null || typeof row.id !== 'number' || typeof row.couponCode !== 'string') return null
  return {
    id: row.id,
    name: text(row.name),
    couponCode: row.couponCode,
    customerPercent: decimal(row.customerPercent),
    organizationPercent: decimal(row.organizationPercent),
    active: row.active === true,
    endsOn: typeof row.endsOn === 'string' && row.endsOn !== '' ? row.endsOn : null,
    note: text(row.note),
    orders: whole(row.orders),
    owedCents: whole(row.owedCents),
    paidCents: whole(row.paidCents),
    balanceCents: whole(row.balanceCents),
    used: row.used === true,
  }
}

export function organizationsOf(payload: unknown): CashbackOrganization[] {
  if (!Array.isArray(payload)) return []
  return payload.map(organizationOf).filter((one): one is CashbackOrganization => one !== null)
}

function bucketOf(payload: unknown): ReportBucket {
  const row = record(payload)
  return {
    orders: whole(row?.orders),
    foodCents: whole(row?.foodCents),
    shareCents: whole(row?.shareCents),
  }
}

function bucketKind(value: unknown): BucketKind {
  return value === 'owed' || value === 'cancelled' ? value : 'pending'
}

/** The report, or null when the answer is not one - which the screen reports rather than draws as nought. */
export function reportOf(payload: unknown): CashbackReport | null {
  const row = record(payload)
  const organization = organizationOf(row?.organization)
  if (row === null || organization === null) return null
  const orders = Array.isArray(row.orders) ? row.orders : []
  const payouts = Array.isArray(row.payouts) ? row.payouts : []
  return {
    organization,
    from: text(row.from),
    to: text(row.to),
    owed: bucketOf(row.owed),
    pending: bucketOf(row.pending),
    cancelled: bucketOf(row.cancelled),
    orders: orders
      .map(record)
      .filter((one): one is Record<string, unknown> => one !== null)
      .map((one) => ({
        orderId: whole(one.orderId),
        orderNumber: text(one.orderNumber),
        createdAt: text(one.createdAt),
        status: text(one.status),
        bucket: bucketKind(one.bucket),
        foodCents: whole(one.foodCents),
        organizationPercent: decimal(one.organizationPercent),
        shareCents: whole(one.shareCents),
      })),
    truncated: row.truncated === true,
    payouts: payouts
      .map(record)
      .filter((one): one is Record<string, unknown> => one !== null)
      .map((one) => ({
        id: whole(one.id),
        amountCents: whole(one.amountCents),
        paidOn: text(one.paidOn),
        note: text(one.note),
        recordedBy: text(one.recordedBy),
      })),
  }
}

// ─── The form ─────────────────────────────────────────────────────────────

export interface OrganizationForm {
  name: string
  couponCode: string
  customerPercent: string
  organizationPercent: string
  active: boolean
  /** `YYYY-MM-DD` or blank. */
  endsOn: string
  note: string
}

/** The owner's own example: 5 % off for the customer, 5 % of the food to the team. */
export const EMPTY_FORM: OrganizationForm = {
  name: '',
  couponCode: '',
  customerPercent: '5',
  organizationPercent: '5',
  active: true,
  endsOn: '',
  note: '',
}

export function formOf(organization: CashbackOrganization): OrganizationForm {
  return {
    name: organization.name,
    couponCode: organization.couponCode,
    customerPercent: String(organization.customerPercent),
    organizationPercent: String(organization.organizationPercent),
    active: organization.active,
    endsOn: organization.endsOn ?? '',
    note: organization.note,
  }
}

/** The code as the server stores it: trimmed and upper case. */
export function normaliseCode(code: string): string {
  return code.trim().toUpperCase()
}

const CODE = /^[A-Z0-9-]{3,30}$/
const DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * A percentage typed the Slovak way or the other, or null when it is not a number with at most two
 * decimals. **A blank is null, not nought** - `Number('')` is 0, and a cleared box would otherwise
 * read as a deliberate 0 %.
 */
export function parsePercent(value: string): number | null {
  const trimmed = value.trim().replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null
  return Number(trimmed)
}

/**
 * What is wrong with the form, in the server's own words - empty when it may be sent. The same
 * rules as `OrganizationCashback`, so the operator is told before the request rather than after.
 */
export function formProblems(form: OrganizationForm): string[] {
  const problems: string[] = []
  const name = form.name.trim()
  if (name.length < 2 || name.length > 80) problems.push(tt('Názov organizácie musí mať 2 až 80 znakov.'))
  if (!CODE.test(normaliseCode(form.couponCode))) {
    problems.push(
      tt('Kód kupónu musí mať 3 až 30 znakov - veľké písmená bez diakritiky, číslice alebo pomlčku.'),
    )
  }
  const customer = parsePercent(form.customerPercent)
  const organization = parsePercent(form.organizationPercent)
  if (customer === null || organization === null || customer <= 0 || organization <= 0 || customer + organization > 100) {
    problems.push(
      tt('Percentá pre zákazníka aj pre organizáciu musia byť väčšie než nula a spolu najviac 100.'),
    )
  }
  if (form.endsOn.trim() !== '' && !DAY.test(form.endsOn.trim())) {
    problems.push(tt('Posledný deň musí byť dátum v tvare RRRR-MM-DD.'))
  }
  return problems
}

/** The body of a new organization. Call only on a form with no problems. */
export function createPayload(form: OrganizationForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    couponCode: normaliseCode(form.couponCode),
    customerPercent: parsePercent(form.customerPercent),
    organizationPercent: parsePercent(form.organizationPercent),
    active: form.active,
    endsOn: form.endsOn.trim() === '' ? null : form.endsOn.trim(),
    note: form.note.trim(),
  }
}

/**
 * Only what changed, so a save cannot rewrite a field nobody touched - and the code only when it
 * changed, because the server refuses any code change once an order has used it. An emptied last
 * day is sent as `''`, which the server reads as *clear it*; an absent one leaves it as it is.
 */
export function changePayload(
  organization: CashbackOrganization,
  form: OrganizationForm,
): Record<string, unknown> {
  const changed: Record<string, unknown> = {}
  if (form.name.trim() !== organization.name) changed.name = form.name.trim()
  if (normaliseCode(form.couponCode) !== organization.couponCode) {
    changed.couponCode = normaliseCode(form.couponCode)
  }
  const customer = parsePercent(form.customerPercent)
  if (customer !== organization.customerPercent) changed.customerPercent = customer
  const share = parsePercent(form.organizationPercent)
  if (share !== organization.organizationPercent) changed.organizationPercent = share
  if (form.active !== organization.active) changed.active = form.active
  if (form.endsOn.trim() !== (organization.endsOn ?? '')) changed.endsOn = form.endsOn.trim()
  if (form.note.trim() !== organization.note) changed.note = form.note.trim()
  return changed
}

// ─── Payouts ──────────────────────────────────────────────────────────────

/** Euros typed with a comma or a point, in cents; null when it is not an amount with at most two decimals. */
export function parseEurosToCents(value: string): number | null {
  const trimmed = value.trim().replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null
  return Math.round(Number(trimmed) * 100)
}

/** Why a payout cannot be recorded, or null - above nought and not above what the organization is owed. */
export function payoutProblem(amount: string, balanceCents: number): string | null {
  const cents = parseEurosToCents(amount)
  if (cents === null || cents <= 0) return tt('Suma výplaty musí byť väčšia než nula.')
  if (cents > balanceCents) {
    return `${tt('Výplata je vyššia ako zostatok, ktorý organizácii patrí')} (${formatCents(balanceCents)}).`
  }
  return null
}

// ─── Words ────────────────────────────────────────────────────────────────

/** How one code splits an order, the sentence under each organization's name. */
export function splitSentence(organization: Pick<CashbackOrganization, 'customerPercent' | 'organizationPercent'>): string {
  return `${tt('Zákazník dostane')} ${formatPercent(organization.customerPercent)} ${tt('zľavu, organizácii patrí')} ${formatPercent(organization.organizationPercent)} ${tt('z jedla')}`
}

export function bucketLabel(kind: BucketKind): string {
  switch (kind) {
    case 'owed':
      return tt('Patrí organizácii')
    case 'pending':
      return tt('Zatiaľ nepatrí - objednávka je otvorená')
    case 'cancelled':
      return tt('Zrušená - nepatrí nič')
  }
}

/** The first day of this month to today, in the browser's own calendar - the report's default range. */
export function thisMonth(today: Date): { from: string; to: string } {
  const pad = (part: number) => String(part).padStart(2, '0')
  const year = today.getFullYear()
  const month = pad(today.getMonth() + 1)
  return { from: `${String(year)}-${month}-01`, to: `${String(year)}-${month}-${pad(today.getDate())}` }
}

// ─── The printed sheet - §9.783 ───────────────────────────────────────────

/** The rows the handover sheet prints: only the orders whose share is owed, in the server's order. */
export function owedRows(report: CashbackReport): ReportOrder[] {
  return report.orders.filter((order) => order.bucket === 'owed')
}

/**
 * What the sheet leaves out, in one sentence, or null - the orders still open, whose share counts once they are
 * closed. A cancelled order is owed nothing and is not mentioned: it changes nothing the organization is paid.
 */
export function openOrdersNote(report: CashbackReport): string | null {
  if (report.pending.orders === 0) return null
  return `${tt('Ďalšie otvorené objednávky v tomto období')}: ${String(report.pending.orders)} (${tt('podiel')} ${formatCents(report.pending.shareCents)}). ${tt('Započítajú sa, keď sa uzavrú.')}`
}

/** The cap, said on paper too: the rows are the first 2 000 of the period and the total covers all of them. */
export function cappedNote(report: CashbackReport): string | null {
  return report.truncated
    ? tt('Zoznam obsahuje prvých 2 000 objednávok obdobia - súčet za obdobie zahŕňa všetky.')
    : null
}

/**
 * True when the printed rows do not add up to the server's own total for the period and the list is not capped.
 * The two come from two reads in one request, so an order closed between them could part them - and a page
 * handed to somebody outside the business must not disagree with itself.
 */
export function sheetDisagrees(report: CashbackReport): boolean {
  if (report.truncated) return false
  const rows = owedRows(report)
  const share = rows.reduce((sum, order) => sum + order.shareCents, 0)
  const food = rows.reduce((sum, order) => sum + order.foodCents, 0)
  return rows.length !== report.owed.orders || share !== report.owed.shareCents || food !== report.owed.foodCents
}

// ─── The row in Kampane - §9.785 ──────────────────────────────────────────

/**
 * What the *Cashback pre organizácie* row in the campaigns table says in its result column.
 *
 * **The balance is the sum of every organization's own `balanceCents`**, which the server computes; it is
 * what the restaurant still has to hand over, and it is the figure that makes the row worth reading without
 * opening it. A failed read says so rather than *no organization*, which would be a claim about money owed
 * made from an answer that never came (§9.528).
 */
export function cashbackRowSummary(organizations: readonly CashbackOrganization[], failed: boolean): string {
  if (failed) return tt('Zoznam organizácií sa nepodarilo načítať.')
  if (organizations.length === 0) return tt('Zatiaľ žiadna organizácia - pridáte ju v Spravovať.')
  const active = organizations.filter((organization) => organization.active).length
  const balance = organizations.reduce((sum, organization) => sum + organization.balanceCents, 0)
  return `${tt('Organizácií')}: ${formatCount(organizations.length)} (${tt('zapnutých')}: ${formatCount(active)}) · ${tt('zostáva vyplatiť')} ${formatCents(balance)}`
}
